import React, { useCallback, useEffect, useState } from 'react'
import { Card, Icon, Button, Tooltip, message, Input, Pagination, Spin, Select, DatePicker, Row, Col, Switch, Modal, Checkbox, notification } from "antd";
import index, { Translate } from "react-translated";
import CustomAgGridTable from '../../Common-Table/CustomAgGridTable';
import '../../Common-Table/CustomAgGridTable.css';
import CustomHeader from '../../CommonHeader/CustomHeader';
import { ALL_ACTIVE_AGENCIES_API_URL } from '../../utils/Api_enums';
import axios from "axios";
import F from "../../Functions";
import { CustomButton } from './util';
import Cookies from "js-cookie";
import { debounce } from 'lodash';
import { removeHeaderTitle } from '../../utils/Constants';
import { clearSharedDashboardDateState } from '../../utils/clearSharedDashboardDateState';

const antIcon = <Icon type="loading" style={{ fontSize: 24 }} spin />;

// Define action types constants
const ACTION_TYPES = {
  SWITCH_TO_AGENCY_MANAGER: 'switchToAgencyManager',
  VIEW_AGENCIES: 'viewAgencies',
  EDIT_USER: 'editUser',
  DELETE_USER: 'deleteUser',
  DEACTIVATE_USER: 'deactivateUser',
  VIEW_USER_DETAILS: 'viewUserDetails',
};

const AgencyManagerListingAgGrid = (props) => {
  const [loading, setLoading] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [pages, setPages] = useState(1);
  const [gridApi, setGridApi] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [agenciesModal, setAgenciesModal] = useState(false);
  const [agenciesModalLoading, setAgenciesModalLoading] = useState(false);
  const [userAgencies, setUserAgencies] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const debouncedSearch = useCallback(
    debounce((searchValue) => {
      setIsSearching(true);
      loadUsers(searchValue)
        .finally(() => {
          setIsSearching(false);
        });
    }, 500),
    []
  );

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearch(value);

    // Only trigger search when input has 3 or more characters
    if (value.trim().length >= 3) {
      setIsSearching(true);
      debouncedSearch(value);
    } else if (value.trim().length === 0) {
      // Reset search when input is empty
      debouncedSearch("");
    }
  };

  const handleSearch = () => {
    if (search.trim() === "") {
      setCurrentPage(1);
      loadUsers(1);
      return;
    }

    setIsSearching(true);
    loadUsers(search)
      .finally(() => {
        setIsSearching(false);
      });
  };

  const handleClearSearch = () => {
    setSearch("");
    setCurrentPage(1);
    loadUsers(1);
  };

  const onGridReady = useCallback((params) => {
    setGridApi(params.api);

    // Resize columns to fit the available width
    setTimeout(() => {
      params.api.sizeColumnsToFit();
    }, 500);

    // Add event listener for window resize to maintain proper sizing
    window.addEventListener('resize', () => {
      setTimeout(() => {
        params.api && params.api.sizeColumnsToFit();
      });
    });

    // If we already have data, make sure it's properly displayed
    if (rowData && rowData.length > 0) {
      setTimeout(() => {
        params.api.setRowData(rowData);
      }, 0);
    } else {
      // If no data yet, fetch it
      loadUsers(1);
    }
  }, [rowData]);

  useEffect(() => {
    prepareGridColumn();
  }, []);

  useEffect(() => {
    loadAgency();
  }, []);

  const loadUsers = (pageOrSearch) => {
    const filters = [];
    // Required filters
    filters.push({ name: "active", op: "==", val: 1 });
    filters.push({ name: "user_type", op: "==", val: "AGENCY_MANAGER" });

    // Determine if pageOrSearch is a page number or search string
    let pageNumber = 1;
    let searchString = '';

    if (typeof pageOrSearch === 'number') {
      pageNumber = pageOrSearch;
      searchString = search;
    } else if (typeof pageOrSearch === 'string') {
      searchString = pageOrSearch;
      setSearch(searchString);
    }

    // Set current page
    setCurrentPage(pageNumber);

    // Remove special characters from the search string
    const searchPurify = F.removeSpecialCharactersFromString(searchString);

    if (searchPurify.trim().length > 0) {
      filters.push({
        or: [
          {
            name: "first_name",
            op: "like",
            val: encodeURIComponent("%" + searchPurify + "%")
          },
          {
            name: "last_name",
            op: "like",
            val: encodeURIComponent("%" + searchPurify + "%")
          },
          {
            name: "email",
            op: "like",
            val: encodeURIComponent("%" + searchPurify + "%")
          }
        ]
      });
    }

    // Build the API URL string
    const api = `/api/users?page=${pageNumber}&q={"filters":${JSON.stringify(filters)}}`;

    // Set loading to true while fetching data
    setLoading(true);

    // Return promise for chaining
    return axios.get(api)
      .then((res) => {
        // Update state with the received data
        if (res.data && res.data.objects && res.data.objects.length > 0) {
          setRowData(res.data.objects);
          setTotalPages(res.data.total_pages);
        } else {
          // If no data from API, show empty state
          setRowData([]);
          setTotalPages(1);
          message.info('No users found matching your criteria');
        }
        return res;
      })
      .catch((err) => {
        console.error("Error loading users:", err);
        message.error('Failed to load users');
        setRowData([]);
        setTotalPages(1);
        return Promise.reject(err);
      })
      .finally(() => {
        // Set loading to false after request finishes
        setLoading(false);
      });
  };

  const loadAgency = () => {
    loadUsers(1);
    setLoading(true);

    axios.get(ALL_ACTIVE_AGENCIES_API_URL)
      .then((res) => {
        setAgencies(res.data);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Action handler for different button actions
  const handleButtonAction = (actionType, data) => {
    localStorage.removeItem("agGridColumnState");
    switch (actionType) {
      case ACTION_TYPES.SWITCH_TO_AGENCY_MANAGER:
        handleSwitchToAgencyManager(data);
        break;
      case ACTION_TYPES.VIEW_AGENCIES:
        handleViewAgencies(data);
        break;
      case ACTION_TYPES.EDIT_USER:
        handleEditUser(data);
        break;
      case ACTION_TYPES.DELETE_USER:
        handleDeleteUser(data);
        break;
      case ACTION_TYPES.DEACTIVATE_USER:
        handleDeactivateUser(data);
        break;
      case ACTION_TYPES.VIEW_USER_DETAILS:
        handleViewUserDetails(data);
        break;
      default:
        console.warn(`Unknown action type: ${actionType}`);
    }
  };

  // Handler functions for the buttons
  const handleSwitchToAgencyManager = (data) => {
    notification.destroy();
    setLoading(true);
    removeHeaderTitle();

    axios.post("/api/switchuser/" + data.id)
      .then((res) => {
        setLoading(false);
        if (res.data.success) {
          const user = res.data.user;

          // Set cookie with user info
          Cookies.set(
            "user_info",
            JSON.stringify({
              first_name: user.first_name,
              last_name: user.last_name,
              email: user.email,
              id: user.id,
              user_type: user.user_type,
              default_date: user.default_date,
              last_login: user.last_login,
              language: user.language,
              active: user.active
            }),
            {
              secure: true,           //  HTTPS only
              sameSite: 'Lax',        // Add this — fixes the scan finding
              path: '/',              // explicit path
            }
          );

          clearSharedDashboardDateState();
          // Redirect to dashboard using window location
          window.location.href = "/dashboard";
        } else {
          message.error(F.translate(`Can't switch to the user.`));
        }
      })
      .catch(() => {
        setLoading(false);
        message.error(F.translate(`Can't switch to the user.`));
      });
  };

  const handleViewAgencies = (data) => {
    setSelectedUser(data);
    setAgenciesModal(true);
    loadUserAgencies(data.id);
  };

  const loadUserAgencies = (userId) => {
    setAgenciesModalLoading(true);

    axios.get("/api/am_agencies_assoc/" + userId)
      .then((res) => {
        let agencies = [];
        for (let i = 0; i < res.data.length; i++) {
          const agency = res.data[i];
          agencies.push(agency.agency_id);
        }
        setUserAgencies(agencies);
      })
      .catch((err) => {
        console.error("Error loading user agencies:", err);
      })
      .finally(() => {
        setAgenciesModalLoading(false);
      });
  };

  const updateAgencyAssoc = (agencyId, checked) => {
    const val = checked ? 1 : 0;

    axios.post(
      "/api/update_am_agency_assoc/" +
      selectedUser.id +
      "/" +
      agencyId +
      "/" +
      val
    )
      .then(() => {
        loadUserAgencies(selectedUser.id);
      })
      .catch((err) => {
        console.error("Error updating agency association:", err);
      });
  };

  const handleCloseModal = () => {
    setAgenciesModal(false);
  };

  // Additional handlers for other actions
  const handleEditUser = (data) => {
    message.info(`Edit functionality for ${data.first_name} ${data.last_name} to be implemented`);
    // Implementation goes here
  };

  const handleDeleteUser = (data) => {
    Modal.confirm({
      title: 'Confirm Delete',
      content: `Are you sure you want to delete user ${data.first_name} ${data.last_name}?`,
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => {
        message.success(`User ${data.first_name} ${data.last_name} would be deleted`);
        // Implementation goes here
      }
    });
  };

  const handleDeactivateUser = (data) => {
    Modal.confirm({
      title: 'Confirm Deactivation',
      content: `Are you sure you want to deactivate user ${data.first_name} ${data.last_name}?`,
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => {
        message.success(`User ${data.first_name} ${data.last_name} would be deactivated`);
        // Implementation goes here
      }
    });
  };

  const handleViewUserDetails = (data) => {
    message.info(`Viewing details for ${data.first_name} ${data.last_name}`);
    // Implementation goes here
  };

  // Custom cell renderer for action buttons
  const ActionButtonsRenderer = (params) => {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: '12px',
        height: '100%',
        padding: '0 10px',
        width: '100%',
        overflow: 'hidden'
      }}>
        <CustomButton
          size="medium"
          icon="swap"
          actionType={ACTION_TYPES.SWITCH_TO_AGENCY_MANAGER}
          data={params.data}
          onClick={handleButtonAction}
          buttonType="switch"
        >
          Switch to Agency Manager
        </CustomButton>
        <CustomButton
          size="medium"
          icon="team"
          actionType={ACTION_TYPES.VIEW_AGENCIES}
          data={params.data}
          onClick={handleButtonAction}
          buttonType="view"
        >
          Agencies
        </CustomButton>
        <CustomButton
          size="medium"
          icon="eye"
          actionType={ACTION_TYPES.VIEW_USER_DETAILS}
          data={params.data}
          onClick={handleButtonAction}
          buttonType="view"
        >
          Details
        </CustomButton>
      </div>
    );
  };

  const prepareGridColumn = () => {
    setColumnDefs([
      {
        headerClass: 'custom-ag-header',
        headerComponent: CustomHeader,
        headerComponentParams: (params) => ({
          headers: ["First Name"],
          colDef: params.column.getColDef(),
          columnApi: params.columnApi,
          field: "first_name",
        }),
        field: "first_name",
        sortable: false,
        filter: true,
        minWidth: 120,
        width: 140,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          padding: '14px 10px'
        }
      },
      {
        headerClass: 'custom-ag-header',
        headerComponent: CustomHeader,
        headerComponentParams: (params) => ({
          headers: ["Last Name"],
          colDef: params.column.getColDef(),
          columnApi: params.columnApi,
          field: "last_name",
        }),
        field: "last_name",
        sortable: false,
        filter: true,
        minWidth: 120,
        width: 140,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          padding: '14px 10px'
        }
      },
      {
        headerClass: 'custom-ag-header',
        headerComponent: CustomHeader,
        headerComponentParams: (params) => ({
          headers: ["Email"],
          colDef: params.column.getColDef(),
          columnApi: params.columnApi,
          field: "email",
        }),
        field: "email",
        sortable: false,
        filter: true,
        minWidth: 180,
        width: 220,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          padding: '14px 10px'
        }
      },
      {
        headerClass: 'custom-ag-header',
        headerComponent: CustomHeader,
        headerComponentParams: (params) => ({
          headers: ["Actions"],
          colDef: params.column.getColDef(),
          columnApi: params.columnApi,
        }),
        field: "actions",
        sortable: false,
        filter: false,
        minWidth: 400,
        width: 450,
        cellRenderer: "actionButtonsRenderer",
        cellStyle: {
          padding: '0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          height: '100%'
        }
      }
    ]);
  };

  return (
    <div>
      <Card type="inner"
        style={{ background: "#FAFAFA" }}
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Icon type="folder" style={{ marginRight: 10, color: "#1890ff" }} />
              <Translate text={("Agency Manager")} />
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Input
                placeholder="Search by name or email"
                value={search}
                onChange={handleSearchInputChange}
                style={{ width: 250, marginRight: 10 }}
                onPressEnter={handleSearch}
              />
              <CustomButton
                type="primary"
                icon="search"
                onClick={handleSearch}
                style={{ marginRight: 10 }}
                theme="action"
                loading={isSearching}
                disabled={search.trim() === ''}
              >
                Search
              </CustomButton>
              <CustomButton
                type="default"
                onClick={handleClearSearch}
                theme="default"
                disabled={isSearching || search.trim() === ''}
              >
                Clear
              </CustomButton>
            </div>
          </div>
        }
        className="tableCard p_and_c_card"
        loading={loading}
      >
        <div className="table-container" style={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 260px)',
          overflow: 'hidden'
        }}>
          <div style={{
            flex: '1 1 auto',
            overflow: 'auto',
            minHeight: 0
          }}>
            <style>
              {`
                .ag-root-wrapper {
                  overflow: visible;
                }
                
                .ag-header-container,
                .ag-center-cols-container {
                  min-width: 950px;
                }
                
                .table-container .ag-root-wrapper {
                  border: none;
                  box-shadow: none;
                }
                
                .table-container .ag-row {
                  border-bottom: 1px solid #f0f0f0;
                }
                
                .table-container .ag-header {
                  border-bottom: 1px solid #f0f0f0;
                  font-weight: 600;
                }
              `}
            </style>
            <CustomAgGridTable
              rowDatas={rowData}
              columnDefss={columnDefs}
              onGridReady={onGridReady}
              frameworkComponents={{
                actionButtonsRenderer: ActionButtonsRenderer
              }}
              gridOptions={{
                rowHeight: 60,
                headerHeight: 52,
                domLayout: 'normal',
                suppressHorizontalScroll: false
              }}
              styles={{
                height: '100%',
                width: '100%',
                overflowX: 'auto'
              }}
            />
          </div>

          {rowData && rowData.length > 0 && (
            <div style={{
              padding: '15px 20px',
              borderTop: '1px solid #f0f0f0',
              marginTop: '10px',
              display: 'flex',
              justifyContent: 'flex-end',
              flex: '0 0 auto',
              paddingRight: '40px'  // Add right padding to move pagination away from the edge
            }}>
              <Pagination
                current={currentPage}
                total={totalPages * pageSize}
                pageSize={pageSize}
                showSizeChanger={false}
                onChange={(page) => {
                  setCurrentPage(page);
                  loadUsers(page);
                }}
              />
            </div>
          )}
        </div>
      </Card>

      {/* Agency Modal */}
      {agenciesModal && (
        <Modal
          title={<Translate text={`Agencies`} />}
          visible={agenciesModal}
          onCancel={handleCloseModal}
          width={600}
          footer={
            <div>
              <Button onClick={handleCloseModal}>
                <Translate text={`Close`} />
              </Button>
            </div>
          }
        >
          <Spin indicator={antIcon} spinning={agenciesModalLoading}>
            <div className="formBox">
              {agencies.map((agency, i) => (
                <div key={i} style={{ marginBottom: '10px' }}>
                  <Checkbox
                    checked={userAgencies.indexOf(agency.id) >= 0}
                    onChange={(e) => {
                      updateAgencyAssoc(agency.id, e.target.checked);
                    }}
                  >
                    {agency.name}
                  </Checkbox>
                </div>
              ))}
            </div>
          </Spin>
        </Modal>
      )}
    </div>
  )
}

export default AgencyManagerListingAgGrid


// WEBPACK FOOTER //
// ./src/scenes/SuperAdmin/AgencyManagerListingAgGrid.js